import React, { useRef, useState } from 'react';
import { ImagePlus, Loader2, Trash2 } from 'lucide-react';
import { ManagerApi } from '../services/managerApi';

const MAX_BYTES = 3 * 1024 * 1024;

/**
 * Upload-first image field — stores the Supabase public URL in company config.
 */
export default function AssetImageField({
  value = '',
  onChange,
  folder = 'general',
  label,
  hint,
  shell = {},
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      setError('Please choose an image file (PNG, JPG, WebP, or GIF).');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('Image must be under 3 MB.');
      return;
    }

    setError('');
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Could not read file'));
        reader.readAsDataURL(file);
      });
      const { publicUrl } = await ManagerApi.uploadAsset({ imageBase64: dataUrl, folder });
      onChange(publicUrl);
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const muted = shell.muted || 'text-ink-muted';
  const labelClass = shell.label || 'text-ink';
  const boxClass = shell.contentBox || 'border border-line bg-canvas rounded-lg';

  return (
    <div className="flex flex-col gap-1.5">
      {label ? <label className={`text-xs font-semibold ${labelClass}`}>{label}</label> : null}
      <div className={`flex items-center gap-3 p-3 ${boxClass}`}>
        <div
          className={`shrink-0 w-16 h-16 rounded-lg border overflow-hidden flex items-center justify-center ${
            shell.input ? 'border-line bg-canvas' : 'border-line bg-surface'
          }`}
        >
          {value ? (
            <img src={value} alt="" className="w-full h-full object-contain p-1" />
          ) : (
            <ImagePlus className={`w-6 h-6 ${muted}`} aria-hidden />
          )}
        </div>
        <div className="flex-1 min-w-0 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-forest-ondark hover:bg-forest-ondark/90 text-white dark:text-canvas-dark disabled:opacity-50 transition-colors"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
            {uploading ? 'Uploading…' : value ? 'Replace' : 'Upload image'}
          </button>
          {value ? (
            <button
              type="button"
              disabled={uploading}
              onClick={() => onChange('')}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                shell.input
                  ? 'border-line text-ink-muted hover:bg-surface'
                  : 'border-line text-ink-muted hover:bg-moss'
              }`}
            >
              <Trash2 className="w-3.5 h-3.5" />
              Remove
            </button>
          ) : null}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="sr-only"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </div>
      {hint ? <p className={`text-[10px] ${muted}`}>{hint}</p> : null}
      {error ? <p className="text-[10px] text-red-700 dark:text-red-400">{error}</p> : null}
    </div>
  );
}
